// Audit & Activity settings section. Activity log is the primary surface;
// retention/denial-logging policy is a low-frequency action and lives behind
// a gear button that opens a modal — keeps the page to one column with no
// secondary tabs.
//
// The hero band (events/24h, top actor, top source, errors + mini chart)
// renders above the timeline so admins land on a quick read of audit
// volume + a drill-down list in the same scroll.
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import { Settings as SettingsIcon, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollToAnchor } from "@/features/settings/hooks/use-scroll-to-anchor";
import { AuditSettingsModal } from "@/features/settings/audit/components/AuditSettingsModal";
import { AuditSummaryBand } from "@/features/settings/audit/components/audit-summary-band";
import { AuditTimeline } from "@/features/audit-log/components/audit-timeline";
import type { AuditSummaryRange } from "@/lib/api/services/audit-summary.service";

function AuditSection() {
  useScrollToAnchor();
  const t = useTranslations("settings.audit");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [summaryRange, setSummaryRange] = useState<AuditSummaryRange>("24h");

  const settingsButton = (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => setSettingsOpen(true)}
      aria-label={t("card.title")}
    >
      <SettingsIcon className="size-4" aria-hidden />
      {t("tabs.settings")}
    </Button>
  );

  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 gap-3">
            <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-[var(--primary)]/10 text-[var(--primary)]">
              <ShieldCheck className="size-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-[var(--foreground)]">
                {t("title")}
              </h1>
              <p className="mt-1 max-w-3xl text-sm leading-6 text-[var(--muted-foreground)]">
                {t("description")}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 justify-start lg:justify-end">
            {settingsButton}
          </div>
        </div>
      </section>
      <AuditSummaryBand
        range={summaryRange}
        onRangeChange={setSummaryRange}
      />
      <AuditTimeline />
      <AuditSettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </div>
  );
}

export const Route = createFileRoute("/_admin/settings/audit")({
  component: AuditSection,
});
